import React, { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight,
    ShieldAlert, Search, Columns3, Eye, EyeOff, Check, ChevronUp, ChevronDown,
    Loader2, AlertTriangle, AlertCircle, Package
} from "lucide-react";
import { motion } from "framer-motion";
import { showError } from "../../../utils/toast";
import baseapi from "../../../services/baseapi";
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown ";


interface ApplicationProblem {
    application_id: number;
    asset_id: number;
    agent_id: number;
    organization_id: number;
    client_id: number;
    app_name: string;
    app_full_name: string;
    app_version: string;
    app_publisher: string;
    software_hashkey: string;
    software_id: number;
    match_type: string;
    matched_by_cpe_id: number;
    cve_id: string;
    published_at: string;
    last_modified: string;
    cve_description: string;
    vuln_status: string;
    cvss_version: string;
    cvss_score: number;
    cvss_severity: string;
    cvss_vector: string;
    exploitability_score: number;
    impact_score: number;
    attack_vector: string;
    attack_complexity: string;
    privileges_required: string;
    user_interaction: string;
    scope: string;
    confidentiality_impact: string;
    integrity_impact: string;
    availability_impact: string;
    cpe_vendor: string;
    cpe_product: string;
    cpe_uri: string;
    cpe_exact_version: string;
    cpe_version_start: string;
    cpe_version_start_inclusive: boolean;
    cpe_version_end: string;
    cpe_version_end_inclusive: boolean;
}

interface ApiResponse {
    total: number;
    data: ApplicationProblem[];
    status: boolean;
}

interface AssetVulnerabilitiesProps {
    agent_id?: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

const AssetVulnerabilities: React.FC<AssetVulnerabilitiesProps> = ({ organization_id, asset_id, client_id }) => {
    const [problems, setProblems] = useState<ApplicationProblem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'published_at', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');




    // Column visibility - IMPORTANT columns shown by default
    const [showColumnFilter, setShowColumnFilter] = useState(false);
    type ColumnKey = 'app_name' | 'app_version' | 'cve_id' | 'cvss_score' | 'cvss_severity' | 'published_at' | 'asset_id' | 'vuln_status' | 'app_publisher' | 'cve_description' | 'attack_vector' | 'cvss_vector' | 'cpe_product';
    const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
        // Default: Show these important columns
        app_name: true,
        app_version: true,
        cve_id: true,
        cvss_score: true,
        cvss_severity: true,
        published_at: true,
        asset_id: true,
        vuln_status: true,
        // Optional: Hide these by default
        app_publisher: false,
        cve_description: false,
        attack_vector: false,
        cvss_vector: false,
        cpe_product: false,
    });

    const columnOptions: { key: ColumnKey; label: string }[] = [
        { key: 'app_name', label: 'App Name' },
        { key: 'app_version', label: 'Version' },
        { key: 'cve_id', label: 'CVE ID' },
        { key: 'cvss_score', label: 'CVSS Score' },
        { key: 'cvss_severity', label: 'Severity' },
        { key: 'published_at', label: 'Published' },
        { key: 'asset_id', label: 'Asset ID' },
        { key: 'vuln_status', label: 'Status' },
        { key: 'app_publisher', label: 'Publisher' },
        { key: 'cve_description', label: 'Description' },
        { key: 'attack_vector', label: 'Attack Vector' },
        { key: 'cvss_vector', label: 'CVSS Vector' },
        { key: 'cpe_product', label: 'CPE Product' },
    ];

    const fetchProblems = useCallback(async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * itemsPerPage;
            const params: any = {
                skip: skip,
                limit: itemsPerPage,
                sort_by: sortConfig.key,
                sort_order: sortConfig.direction,
                query: JSON.stringify({

                    organization_id: organization_id,
                    client_id: client_id,
                    asset_id: asset_id,
                })
            };



            if (debouncedSearch) {
                params.search = debouncedSearch;
            }

            const response = await baseapi.get<ApiResponse>("/assets/application_problems", { params });
            if (response.data.status) {
                setProblems(response.data.data || []);
                setTotal(response.data.total || 0);
            }
        } catch (error: any) {
            showError(error.message || "Failed to fetch application problems");
            setProblems([]);
            setTotal(0);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [currentPage, itemsPerPage, sortConfig, debouncedSearch]);

    useEffect(() => {
        fetchProblems();
    }, [fetchProblems]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const handleRefresh = () => {
        setIsRefreshing(true);
        fetchProblems();
    };

    const handleSort = (key: string) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        const totalPages = Math.ceil(total / itemsPerPage);
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return "-";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-US", {
                year: "numeric", month: "short", day: "numeric"
            });
        } catch {
            return "Invalid Date";
        }
    };

    const getSeverityStyle = (severity: string | null, score: number | null) => {
        if (!severity || !score) return "bg-gray-800 text-gray-200 border-gray-700";
        const s = severity.toLowerCase();
        if (s.includes("critical") || score! >= 9.0) return "bg-red-500/10 text-red-400 border-red-500/30";
        if (s.includes("high") || score! >= 7.0) return "bg-orange-500/10 text-orange-400 border-orange-500/30";
        if (s.includes("medium") || score! >= 4.0) return "bg-yellow-500/10 text-yellow-400 border-yellow-500/30";
        return "bg-green-500/10 text-green-400 border-green-500/30";
    };

    const getSeverityIcon = (severity: string | null, score: number | null) => {
        if (!severity || !score) return <ShieldAlert size={10} className="text-gray-200" />;
        const s = severity.toLowerCase();
        if (s.includes("critical") || score! >= 9.0) return <AlertTriangle size={10} className="text-red-400" />;
        if (s.includes("high") || score! >= 7.0) return <AlertCircle size={10} className="text-orange-400" />;
        if (s.includes("medium") || score! >= 4.0) return <AlertCircle size={10} className="text-yellow-400" />;
        return <Check size={10} className="text-green-400" />;
    };

    const totalPages = Math.ceil(total / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, total);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="p-3 pb-20 bg-gray-900 min-h-screen"
        >
            <div className="w-full mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3 border-b border-gray-700 pb-2">
                    <div className="flex items-center">
                        <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                            <ShieldAlert className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-sm font-bold text-white leading-tight">Application Vulnerabilities</h2>
                            {!loading && <span className="text-xs font-bold text-gray-200">({total})</span>}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleRefresh}
                            className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                            title="Refresh data"
                        >
                            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin text-orange-500' : ''}`} />
                        </button>

                        <div className="relative w-64">
                            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                placeholder="Search apps, CVEs..."
                                className="w-64 bg-gray-900 border border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500   transition-colors h-8"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="relative">
                            <button
                                onClick={() => setShowColumnFilter(!showColumnFilter)}
                                className="p-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 text-gray-200 transition-all flex items-center gap-2"
                            >
                                <Columns3 className="w-3.5 h-3.5" />
                                <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
                            </button>
                            {showColumnFilter && (
                                <div className="absolute top-full mt-2 right-0 w-52 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[110] p-1.5">
                                    <div className="px-3 py-1.5 text-[9px] font-bold text-gray-200 uppercase border-b border-gray-800 mb-1">
                                        Toggle Columns
                                    </div>
                                    {columnOptions.map((option) => (
                                        <button
                                            key={option.key}
                                            onClick={() => setVisibleColumns(prev => ({ ...prev, [option.key]: !prev[option.key] }))}
                                            className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-gray-200 hover:bg-gray-800 rounded-lg transition-colors"
                                        >
                                            <div className="flex items-center gap-2.5">
                                                {visibleColumns[option.key] ? <Eye className="w-3 h-3 text-orange-500" /> : <EyeOff className="w-3 h-3 text-gray-600" />}
                                                {option.label}
                                            </div>
                                            <div className={`w-3 h-3 rounded border flex items-center justify-center ${visibleColumns[option.key] ? 'bg-orange-600 border-orange-600' : 'border-gray-700'}`}>
                                                {visibleColumns[option.key] && <Check className="w-2.5 h-2.5 text-white" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-gray-800/40 border border-gray-700 rounded-t-xl overflow-hidden shadow-2xl">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-[10px] border-collapse">
                            <thead className="bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-gray-700/50">
                                    {columnOptions.map(({ key, label }) => (
                                        visibleColumns[key] && (
                                            <th
                                                key={key}
                                                onClick={() => handleSort(key)}
                                                className={`px-3 py-2 text-[9px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer select-none hover:text-gray-100 transition-colors ${key === 'cvss_score' ? 'text-center' : ''}`}
                                            >
                                                <div className={`flex items-center gap-1 ${key === 'cvss_score' ? 'justify-center' : ''}`}>
                                                    {label}
                                                    {sortConfig.key === key ? (
                                                        sortConfig.direction === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />
                                                    ) : (
                                                        <ChevronDown size={10} className="opacity-20" />
                                                    )}
                                                </div>
                                            </th>
                                        )
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/30">
                                {loading ? (
                                    <tr>
                                        <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-20 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                                                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200 italic">Loading vulnerabilities...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : problems.length === 0 ? (
                                    <tr>
                                        <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-20 text-center text-gray-200 font-bold uppercase tracking-widest italic">
                                            No vulnerabilities found
                                        </td>
                                    </tr>
                                ) : (
                                    problems.map((problem, idx) => (
                                        <tr key={`${problem.application_id}-${problem.asset_id}-${idx}`} className="hover:bg-gray-800/60 transition-colors group">
                                            {visibleColumns.app_name && (
                                                <td className="px-3 py-1.5 font-medium text-gray-200">
                                                    <div className="flex items-center gap-2">
                                                        <Package size={12} className="text-orange-400 flex-shrink-0" />
                                                        <span className="truncate max-w-[200px]" title={problem.app_full_name || problem.app_name}>
                                                            {problem.app_name}
                                                        </span>
                                                    </div>
                                                </td>
                                            )}
                                            {visibleColumns.app_version && (
                                                <td className="px-3 py-1.5 font-mono text-[9px] text-gray-200 bg-gray-900 px-2 py-0.5 rounded border border-gray-800">
                                                    {problem.app_version || "Unknown"}
                                                </td>
                                            )}
                                            {visibleColumns.cve_id && (
                                                <td className="px-3 py-1.5">
                                                    <span
                                                        onClick={() => window.open(`https://nvd.nist.gov/vuln/detail/${problem.cve_id}`, '_blank')}
                                                        className="font-mono  cursor-pointer text-[10px] font-bold text-red-400 bg-red-500/10 px-2 py-0.5 rounded border border-red-500/30">
                                                        {problem.cve_id || "-"}
                                                    </span>
                                                </td>
                                            )}
                                            {visibleColumns.cvss_score && (
                                                <td className="px-3 py-1.5 text-center font-bold text-[15px] text-orange-400">
                                                    {problem.cvss_score?.toFixed(1) || "-"}
                                                </td>
                                            )}
                                            {visibleColumns.cvss_severity && (
                                                <td className="px-3 py-1.5">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${getSeverityStyle(problem.cvss_severity, problem.cvss_score)}`}>
                                                        {getSeverityIcon(problem.cvss_severity, problem.cvss_score)}
                                                        {problem.cvss_severity || "Unknown"}
                                                    </span>
                                                </td>
                                            )}
                                            {visibleColumns.published_at && (
                                                <td className="px-3 py-1.5 text-gray-200 font-mono text-[9px]">
                                                    {formatDate(problem.published_at || "")}
                                                </td>
                                            )}
                                            {visibleColumns.asset_id && (
                                                <td className="px-3 py-1.5 text-center">
                                                    <span className="bg-blue-500/10 text-blue-400 border border-blue-500/30 px-2 py-0.5 rounded text-[9px] font-bold uppercase">
                                                        #{problem.asset_id}
                                                    </span>
                                                </td>
                                            )}
                                            {visibleColumns.vuln_status && (
                                                <td className="px-3 py-1.5">
                                                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${problem.vuln_status === 'active' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-gray-800 text-gray-200 border-gray-700'}`}>
                                                        {problem.vuln_status || "Unknown"}
                                                    </span>
                                                </td>
                                            )}
                                            {visibleColumns.app_publisher && (
                                                <td className="px-3 py-1.5 text-gray-200 text-[9px] truncate max-w-[120px]">
                                                    {problem.app_publisher || "-"}
                                                </td>
                                            )}
                                            {visibleColumns.cve_description && (
                                                <td className="px-3 py-1.5 max-w-[200px]">
                                                    <div className="text-[9px] text-gray-200 line-clamp-2 leading-tight" title={problem.cve_description}>
                                                        {problem.cve_description || "-"}
                                                    </div>
                                                </td>
                                            )}
                                            {visibleColumns.attack_vector && (
                                                <td className="px-3 py-1.5 text-[9px] font-mono text-gray-200 uppercase">
                                                    {problem.attack_vector || "-"}
                                                </td>
                                            )}
                                            {visibleColumns.cvss_vector && (
                                                <td className="px-3 py-1.5 text-[9px] font-mono text-gray-200 bg-gray-950 px-1.5 py-0.5 rounded border border-gray-800 truncate max-w-[140px]">
                                                    {problem.cvss_vector || "-"}
                                                </td>
                                            )}
                                            {visibleColumns.cpe_product && (
                                                <td className="px-3 py-1.5 text-[9px] text-gray-200 truncate max-w-[120px]">
                                                    {problem.cpe_product || "-"}
                                                </td>
                                            )}
                                        </tr>
                                    )))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                <div className="bg-gray-800/50 border rounded-b-xl border-gray-700 px-3 py-1 flex items-center justify-between mt-0">
                    <div className="flex items-center gap-4">
                        <ItemsPerPageDropdown
                            value={itemsPerPage}
                            onChange={(val: any) => {
                                setItemsPerPage(val);
                                setCurrentPage(1);
                            }}
                        />
                        <span className="text-[10px] text-gray-200 font-bold uppercase tracking-widest">
                            <span className="text-gray-200">{startIndex + 1}</span> - <span className="text-gray-200">{endIndex}</span> OF {total}
                        </span>
                    </div>

                    <div className="flex items-center gap-1">
                        <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 text-gray-200 hover:text-white disabled:opacity-30 transition-colors">
                            <ChevronsLeft className="w-4 h-4" />
                        </button>
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 text-gray-200 hover:text-white disabled:opacity-30 transition-colors">
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">
                            {currentPage}
                        </div>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} className="p-1.5 text-gray-200 hover:text-white disabled:opacity-30 transition-colors">
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0} className="p-1.5 text-gray-200 hover:text-white disabled:opacity-30 transition-colors">
                            <ChevronsRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default AssetVulnerabilities;